<script setup>
  import {computed, nextTick, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import LayoutPrintRecoveryCode from "@components/Layout/Components/PrintRecoveryCode.vue";
  import ComponentPasswordStrength from "@components/PasswordStrength/PasswordStrength.vue";
  import {router} from "@inertiajs/vue3";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "CryptDialogsOnboarding",
  });

  const props = defineProps({
    privacy: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    posIntegration: {
      type: Object,
      required: true,
    },
  });

  const refCryptDialog = ref(null);
  const passwordInput = ref(null);

  const passwordPlaintext = ref("");
  const passwordConfirmation = ref("");
  const passwordConfirmationErrorMessage = ref("");

  const strengthCheckerLoaded = ref(false);
  const passwordStrength = ref(0);

  const valid = computed(() => validPasswordMinLength.value && props.privacy.getErrorMessage() === null);

  const validPasswordConfirmation = computed(() => passwordConfirmationErrorMessage.value === null);

  const validPasswordMinLength = computed(
    () => passwordPlaintext.value.trim().length >= props.privacy.getPasswordMinLength(),
  );

  const isPasswordStrongEnough = computed(() => passwordStrength.value >= import.meta.env.VITE_PASSWORD_STRENGTH);

  watch(
    () => passwordPlaintext.value,
    (newValue) => {
      comparePasswords();
      props.privacy.plaintextPassword(newValue);
    },
  );

  watch(
    () => passwordConfirmation.value,
    () => {
      comparePasswords();
    },
  );

  const open = () => {
    refCryptDialog.value.open();

    nextTick(() => {
      passwordInput.value?.focus();
    });
  };

  const close = () => {
    if (refCryptDialog.value) refCryptDialog.value.close();
  };

  const closeSuccessDialog = () => {
    props.privacy.setCurrentStatus("VERIFICATION_VALID");
    router.visit(route("dashboard.index"));
    close();
  };

  const checkOnMinLength = () => {
    if (validPasswordMinLength.value === false) {
      props.privacy.setErrorMessage("Ihr Passwort muss mindestens 8 Zeichen lang sein.");
      props.privacy.setCurrentStatus("ENCRYPTION_INVALID");
    }
  };

  const confirmPassword = () => {
    if (!valid.value) {
      props.privacy.setErrorMessage("Ihr Passwort muss mindestens 8 Zeichen lang sein.");
      props.privacy.setCurrentStatus("ENCRYPTION_INVALID");
    } else if (validPasswordConfirmation.value && props.privacy.getRecoveryCodeHasBeenPrinted()) {
      // double check: only set password, if confirmation
      // is set and recovery code has been printed
      props.privacy.setPassword();
    }
  };

  const comparePasswords = () => {
    if (validPasswordMinLength.value && passwordConfirmation.value === "") {
      passwordConfirmationErrorMessage.value = "Bitte wiederholen Sie Ihre Eingabe.";
    } else if (validPasswordMinLength.value && passwordPlaintext.value !== passwordConfirmation.value) {
      passwordConfirmationErrorMessage.value = "Passwort stimmt nicht überein.";
    } else {
      passwordConfirmationErrorMessage.value = null;
    }
  };

  const printRecoveryCode = () => {
    globalThis.print();
    props.privacy.setRecoveryCodeHasBeenPrinted(true);
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog ref="refCryptDialog" dialog-width="medium" :test-id="getTestId('onboarding')">
    <template #title>
      <span class="hidden print:inline">MediCheck</span>
      Datenschutzpasswort
    </template>

    <template #content>
      <div class="print:hidden">
        <template v-if="isLoading">
          <div class="text-center">
            <MedSpinner size="3xl" :data-test="getTestId('onboarding-dialog-spinner')" />
          </div>
        </template>

        <template v-else-if="privacy.hasCryptStatus('ENCRYPTION_SET')">
          <p class="flex items-center space-x-3">
            <ComponentIcon>check</ComponentIcon>
            <span>Ihr Datenschutzpasswort wurde festgelegt</span>
          </p>

          <p class="mt-4">
            Bitte
            <strong>notieren Sie sich jetzt Ihr Passwort</strong>
            und hinterlegen Sie es mit dem ausgedruckten Recovery-Code an einem sicheren Ort.
          </p>

          <p class="mt-4">
            Bei Verlust Ihres Datenschutzpassworts sind die personenbezogenen Daten Ihrer Patienten irreversibel
            verloren. Auch Mitarbeiter von pharma4u (MediCheck) haben keine technische Möglichkeit, Ihre lokal
            verschlüsselten Daten zu lesen oder wiederherzustellen.
          </p>

          <ComponentAlert class="mt-4" type="info">
            <p>
              <strong>
                Nutzen Sie den MediCheck Connect Core für Medikationsanalysen bereits über Ihre Warenwirtschaft?
              </strong>
            </p>

            <p class="mt-2">
              Um alle Vorteile der
              <strong>Pro-Version</strong>
              nutzen zu können, empfehlen wir, Ihren bestehenden MediCheck-Account der Warenwirtschaft mit Ihrem soeben
              neu eingerichteten Account der pharma4u zu verknüpfen:
            </p>

            <p class="mt-2">
              <strong>So verknüpfen Sie Ihre Accounts:</strong>
            </p>
            <ol class="list-decimal pl-5">
              <li>Öffnen Sie den MediCheck über Ihre Warenwirtschaft.</li>
              <li>
                Öffnen Sie rechts oben im Hauptmenü über das Symbol "
                <ComponentIcon class="inline-block align-middle text-xl">account_circle</ComponentIcon>
                " die persönlichen Einstellungen.
              </li>
              <li>
                Wählen Sie dort die Option
                <strong>"Accounts verknüpfen"</strong>
                und folgen Sie Schritt für Schritt den Anweisungen, um die Verknüpfung durchzuführen.
              </li>
            </ol>

            <p class="mt-2">
              <strong>Bitte beachten:</strong>
              Für den Verknüpfungsprozess benötigen Sie das Datenschutzpasswort aus Ihrer Warenwirtschaft und das
              Datenschutzpasswort des pharma4u-Accounts, das Sie gerade vergeben haben.
            </p>
          </ComponentAlert>
        </template>

        <template v-else>
          <p>
            Um personenbezogene Daten Ihrer Patienten verschlüsselt an MediCheck zu übertragen, legen Sie nun bitte
            <strong>einmalig</strong>
            Ihr Datenschutzpasswort fest.
          </p>

          <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
          <form autocomplete="off" @submit.prevent="confirmPassword" @keydown.enter.prevent="confirmPassword">
            <ComponentInput
              ref="passwordInput"
              v-model="passwordPlaintext"
              class="mt-4"
              type="password"
              label="Passwort"
              autocomplete="off"
              :validation="privacy.getErrorMessage()"
              :maxlength="64"
              :show-maxlength-count="false"
              :show-max-length-message="'Die maximal mögliche Zeichenanzahl beträgt 64 Zeichen.'"
              :test-id="getTestId('onboarding-dialog-password-plaintext')"
              @blur="checkOnMinLength"
            />

            <div class="mb-2 text-xs text-gray-500">
              Mind. 8 Zeichen.
              <br />
              Mind. Passwortstärke mittel (orange)
            </div>

            <ComponentPasswordStrength
              v-show="strengthCheckerLoaded && passwordPlaintext.length > 0"
              :password="passwordPlaintext"
              @loaded="strengthCheckerLoaded = true"
              @score-changed="passwordStrength = $event"
            />

            <template v-if="isPasswordStrongEnough">
              <ComponentInput
                v-model="passwordConfirmation"
                class="mt-8"
                type="password"
                label="Passwort wiederholen"
                :validation="passwordConfirmationErrorMessage"
                :maxlength="64"
                :show-maxlength-count="false"
                :show-max-length-message="'Die maximal mögliche Zeichenanzahl beträgt 64 Zeichen.'"
                :test-id="getTestId('onboarding-dialog-password-confirmation')"
              />
            </template>
          </form>

          <p class="mt-8">
            <!-- TODO: remove line for multi account feature -->
            Beachten Sie, dass dieses Passwort allen Personen bekannt sein muss, die mit MediCheck arbeiten.
          </p>

          <p class="mt-4">
            Notieren Sie sich das Passwort und
            <strong>drucken Sie den Recovery-Code aus</strong>
            . Hinterlegen Sie beides an einem sicheren Ort.
            <br />
            Bei einem Verlust Ihres Datenschutzpassworts sind die personenbezogenen Daten Ihrer Patienten irreversibel
            verloren. Auch Mitarbeiter von pharma4u (MediCheck) haben keine technische Möglichkeit, Ihre lokal
            verschlüsselten Daten zu lesen oder wiederherzustellen.
          </p>
        </template>
      </div>

      <LayoutPrintRecoveryCode :recovery-code="privacy.getRecoveryCode()" :test-id="getTestId('onboarding-dialog')" />
    </template>

    <template #actions>
      <template v-if="privacy.hasCryptStatus('ENCRYPTION_SET')">
        <MedButton
          variant="primary"
          :data-test="getTestId('onboarding-dialog-success-dialog-close-button')"
          icon="lock"
          @click="closeSuccessDialog()"
        >
          OK, verstanden
        </MedButton>
      </template>

      <template v-if="privacy.hasOneOfCryptStatus(['ENCRYPTION_NOT_SET', 'ENCRYPTION_INVALID'])">
        <MedButton
          :disabled="!valid || !validPasswordConfirmation || !privacy.getRecoveryCodeHasBeenPrinted()"
          variant="primary"
          :data-test="getTestId('onboarding-dialog-confirm-password-button')"
          icon="lock_open"
          @click="confirmPassword()"
        >
          Passwort festlegen
        </MedButton>

        <MedButton
          variant="primary"
          :disabled="!valid || !validPasswordConfirmation"
          :data-test="getTestId('onboarding-dialog-print-recovery-code-button')"
          :icon="privacy.getRecoveryCodeHasBeenPrinted() ? 'check' : 'print'"
          @click="printRecoveryCode()"
        >
          Drucken
        </MedButton>
      </template>
    </template>
  </ComponentDialog>
</template>
